import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  EFConnectableSide,
  EFConnectionBehavior,
  EFConnectionType,
  EFMarkerType,
  FFlowModule,
  type FCreateConnectionEvent,
  provideFFlow,
  withFCanvas,
} from '@foblex/flow';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroArrowLeft,
  heroBeaker,
  heroBolt,
  heroBookOpen,
  heroCheckCircle,
  heroChevronRight,
  heroCircleStack,
  heroCpuChip,
  heroDocumentCheck,
  heroDocumentDuplicate,
  heroExclamationTriangle,
  heroFlag,
  heroForward,
  heroFunnel,
  heroLink,
  heroLockClosed,
  heroPlus,
  heroShieldCheck,
  heroSparkles,
  heroTrash,
  heroUserGroup,
  heroWrenchScrewdriver,
  heroXMark,
} from '@ng-icons/heroicons/outline';
import { finalize, forkJoin, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import {
  ToastrService,
  UiBadge,
  UiButton,
  UiDrawer,
  UiDrawerBody,
  UiDrawerFooter,
  UiDrawerHeader,
  UiDrawerOverlay,
  UiHint,
  UiIconButton,
  UiInput,
  UiLabel,
  UiNativeSelect,
  UiPanelCard,
  UiSpinner,
  UiSwitch,
  UiSwitchThumb,
  UiTextarea,
} from '../../../shared/ui';
import {
  AgentsApiService,
  type AgentArchetypeVersion,
  type ApprovedLlmModel,
  type ArchetypeGraphEdge,
  type ArchetypeGraphNode,
  type ArchetypeGraphSpec,
  type ArchetypeNodeType,
  type ArchetypeValidationIssue,
  type ManagedAgentArchetype,
} from '../agents-api.service';

type NodeTemplate = {
  type: ArchetypeNodeType;
  label: string;
  description: string;
  icon: string;
};

const NODE_TEMPLATES: readonly NodeTemplate[] = [
  { type: 'knowledge', label: 'Conhecimento', description: 'Recupera contexto das bibliotecas vinculadas.', icon: 'heroBookOpen' },
  { type: 'llm', label: 'Raciocínio LLM', description: 'Interpreta contexto e produz uma saída intermediária.', icon: 'heroSparkles' },
  { type: 'condition', label: 'Condição', description: 'Divide o fluxo com uma regra determinística.', icon: 'heroFunnel' },
  { type: 'tool', label: 'Ferramenta', description: 'Planeja o uso de uma tool permitida na versão.', icon: 'heroWrenchScrewdriver' },
  { type: 'handoff', label: 'Handoff', description: 'Encaminha o atendimento para uma equipe ou pessoa.', icon: 'heroUserGroup' },
  { type: 'response', label: 'Compor resposta', description: 'Gera a resposta final usando o runtime seguro.', icon: 'heroForward' },
];

const NODE_LABELS: Record<ArchetypeNodeType, string> = {
  start: 'Entrada',
  load_context: 'Carregar contexto',
  guardrail: 'Proteção',
  knowledge: 'Conhecimento',
  llm: 'Raciocínio LLM',
  condition: 'Condição',
  tool: 'Ferramenta',
  handoff: 'Handoff',
  response: 'Compor resposta',
  validate: 'Validar resposta',
  end: 'Término',
};

const NODE_ICONS: Record<ArchetypeNodeType, string> = {
  start: 'heroFlag',
  load_context: 'heroCircleStack',
  guardrail: 'heroShieldCheck',
  knowledge: 'heroBookOpen',
  llm: 'heroSparkles',
  condition: 'heroFunnel',
  tool: 'heroWrenchScrewdriver',
  handoff: 'heroUserGroup',
  response: 'heroForward',
  validate: 'heroDocumentCheck',
  end: 'heroCheckCircle',
};

@Component({
  selector: 'app-archetype-editor-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FFlowModule,
    NgClass,
    NgIcon,
    UiBadge,
    UiButton,
    UiDrawer,
    UiDrawerBody,
    UiDrawerFooter,
    UiDrawerHeader,
    UiDrawerOverlay,
    UiHint,
    UiIconButton,
    UiInput,
    UiLabel,
    UiNativeSelect,
    UiPanelCard,
    UiSpinner,
    UiSwitch,
    UiSwitchThumb,
    UiTextarea,
  ],
  providers: [
    provideFFlow(withFCanvas()),
    provideIcons({
      heroArrowLeft,
      heroBeaker,
      heroBolt,
      heroBookOpen,
      heroCheckCircle,
      heroChevronRight,
      heroCircleStack,
      heroCpuChip,
      heroDocumentCheck,
      heroDocumentDuplicate,
      heroExclamationTriangle,
      heroFlag,
      heroForward,
      heroFunnel,
      heroLink,
      heroLockClosed,
      heroPlus,
      heroShieldCheck,
      heroSparkles,
      heroTrash,
      heroUserGroup,
      heroWrenchScrewdriver,
      heroXMark,
    }),
  ],
  templateUrl: './archetype-editor.page.html',
  styleUrl: './archetype-editor.page.css',
})
export class ArchetypeEditorPage {
  private readonly api = inject(AgentsApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toast = inject(ToastrService);

  readonly connectionType = EFConnectionType.SEGMENT;
  readonly connectionBehavior = EFConnectionBehavior.FIXED;
  readonly inputSide = EFConnectableSide.LEFT;
  readonly outputSide = EFConnectableSide.RIGHT;
  readonly markerEnd = EFMarkerType.END;
  readonly canvasPosition = { x: 20, y: 40 };
  readonly canvasScale = 0.76;
  readonly nodeTemplates = NODE_TEMPLATES;

  readonly archetype = signal<ManagedAgentArchetype | null>(null);
  readonly version = signal<AgentArchetypeVersion | null>(null);
  readonly models = signal<ApprovedLlmModel[]>([]);
  readonly nodes = signal<ArchetypeGraphNode[]>([]);
  readonly edges = signal<ArchetypeGraphEdge[]>([]);
  readonly selectedNodeId = signal<string | null>(null);
  readonly issues = signal<ArchetypeValidationIssue[]>([]);
  readonly loading = signal(true);
  readonly busyAction = signal<string | null>(null);
  readonly dirty = signal(false);
  readonly libraryOpen = signal(false);
  readonly testOpen = signal(false);
  readonly testMessage = signal('Olá, preciso de ajuda para entender qual é o próximo passo.');
  readonly testOutput = signal('');
  readonly createName = signal('');
  readonly createDescription = signal('');
  readonly name = signal('');
  readonly description = signal('');
  readonly jsonErrors = signal<Record<string, string>>({});

  readonly isNew = this.route.snapshot.paramMap.get('archetypeId') === null;
  readonly selectedNode = computed(() => this.nodes().find((node) => node.id === this.selectedNodeId()) ?? null);
  readonly hasJsonErrors = computed(() => Object.keys(this.jsonErrors()).length > 0);
  readonly readOnly = computed(() => this.archetype()?.origin === 'system' || this.version()?.status !== 'draft');
  readonly canPublish = computed(() => {
    const version = this.version();
    return Boolean(
      version
      && version.status === 'draft'
      && version.validationStatus === 'valid'
      && version.validatedGraphHash === version.graphHash
      && version.testedGraphHash === version.graphHash
      && !this.dirty(),
    );
  });
  readonly statusText = computed(() => {
    const version = this.version();
    if (!version) return 'Sem versão';
    if (version.status === 'published') return `v${version.version} publicada`;
    if (this.dirty()) return `v${version.version} · alterações não salvas`;
    return `v${version.version} · rascunho salvo`;
  });

  constructor() {
    if (this.isNew) {
      this.loading.set(false);
      return;
    }
    const id = this.route.snapshot.paramMap.get('archetypeId');
    if (id) this.load(id);
  }

  back(): void {
    void this.router.navigate(['/agentes/arquetipos']);
  }

  create(): void {
    const name = this.createName().trim();
    if (!name) {
      this.toast.info('Informe um nome para o arquétipo.');
      return;
    }
    this.busyAction.set('create');
    this.api.createArchetype({ name, description: this.createDescription().trim() || null })
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.busyAction.set(null)))
      .subscribe({
        next: (created) => void this.router.navigate(['/agentes/arquetipos', created.id]),
        error: () => this.toast.error('Não foi possível criar o arquétipo.'),
      });
  }

  save(): void {
    this.busyAction.set('save');
    this.persistDraft().pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.busyAction.set(null)),
    ).subscribe({
      next: () => this.toast.success('Rascunho salvo.'),
      error: (error: { status?: number }) => this.handleWriteError(error),
    });
  }

  validate(): void {
    this.busyAction.set('validate');
    this.persistDraft().pipe(
      switchMap((version) => this.api.validateArchetypeDraft(version.archetypeId, version.id, version.revision)),
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.busyAction.set(null)),
    ).subscribe({
      next: (result) => {
        this.version.set(result.version);
        this.issues.set(result.issues);
        if (result.valid) {
          this.toast.success('Fluxo válido.');
        } else {
          this.toast.error('O fluxo ainda tem pendências.');
        }
      },
      error: (error: { status?: number }) => this.handleWriteError(error),
    });
  }

  runTest(): void {
    const message = this.testMessage().trim();
    if (!message) return;
    this.busyAction.set('test');
    this.testOutput.set('');
    this.persistDraft().pipe(
      switchMap((version) => this.ensureValidated(version)),
      switchMap((version) => this.api.testArchetypeDraft(version.archetypeId, version.id, {
        expectedRevision: version.revision,
        message,
      })),
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.busyAction.set(null)),
    ).subscribe({
      next: (result) => {
        this.version.set(result.version);
        this.testOutput.set(result.output);
        this.toast.success('Smoke test concluído.');
      },
      error: (error: { status?: number }) => this.handleWriteError(error),
    });
  }

  publish(): void {
    const version = this.version();
    if (!version || !this.canPublish()) {
      this.toast.info('Salve, valide e teste esta revisão antes de publicar.');
      return;
    }
    this.busyAction.set('publish');
    this.api.publishArchetypeDraft(version.archetypeId, version.id, {
      expectedRevision: version.revision,
      idempotencyKey: crypto.randomUUID(),
    }).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.busyAction.set(null)),
    ).subscribe({
      next: (published) => {
        this.version.set(published);
        this.dirty.set(false);
        this.toast.success(`Versão ${published.version} publicada.`);
        this.load(published.archetypeId);
      },
      error: (error: { status?: number }) => this.handleWriteError(error),
    });
  }

  createDraft(): void {
    const archetype = this.archetype();
    if (!archetype || archetype.origin !== 'business') return;
    this.busyAction.set('draft');
    this.api.createArchetypeDraft(archetype.id).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.busyAction.set(null)),
    ).subscribe({
      next: (version) => this.applyVersion(version),
      error: () => this.toast.error('Não foi possível criar o rascunho.'),
    });
  }

  duplicate(): void {
    const archetype = this.archetype();
    if (!archetype) return;
    this.busyAction.set('duplicate');
    this.api.duplicateArchetype(archetype.id).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.busyAction.set(null)),
    ).subscribe({
      next: (created) => void this.router.navigate(['/agentes/arquetipos', created.id]),
      error: () => this.toast.error('Não foi possível duplicar o template.'),
    });
  }

  selectNode(nodeId: string): void {
    this.selectedNodeId.set(nodeId);
  }

  closeInspector(): void {
    this.selectedNodeId.set(null);
  }

  moveNode(nodeId: string, position: { x: number; y: number }): void {
    if (this.readOnly()) return;
    this.nodes.update((nodes) => nodes.map((node) => node.id === nodeId ? { ...node, position } : node));
    this.markDirty();
  }

  createConnection(event: FCreateConnectionEvent): void {
    if (this.readOnly()) return;
    const source = portNodeId(event.sourceId);
    const target = portNodeId(event.targetId);
    if (!source || !target || source === target) return;
    const sourcePort = event.sourceId.endsWith('::true') ? 'true' : event.sourceId.endsWith('::false') ? 'false' : undefined;
    this.edges.update((edges) => [
      ...edges.filter((edge) => !(edge.source === source && edge.sourcePort === sourcePort)),
      { id: `edge-${source}-${target}-${Date.now()}`, source, target, ...(sourcePort ? { sourcePort } : {}) },
    ]);
    this.markDirty();
  }

  addNode(template: NodeTemplate): void {
    if (this.readOnly()) return;
    const selected = this.selectedNode();
    const id = `${template.type}-${Date.now().toString(36)}`;
    const position = selected
      ? { x: selected.position.x + 300, y: selected.position.y + (this.nodes().length % 3) * 70 }
      : { x: 300, y: 180 + this.nodes().length * 40 };
    const node: ArchetypeGraphNode = {
      id,
      type: template.type,
      name: template.label,
      protected: false,
      position,
      config: defaultConfig(template.type, this.models()[0]?.modelKey),
    };
    const existingNext = selected ? this.edges().find((edge) => edge.source === selected.id) : null;
    this.nodes.update((nodes) => [...nodes, node]);
    if (selected) {
      this.edges.update((edges) => [
        ...edges.filter((edge) => edge.id !== existingNext?.id),
        { id: `edge-${selected.id}-${id}`, source: selected.id, target: id },
        ...(existingNext ? [{ ...existingNext, id: `edge-${id}-${existingNext.target}`, source: id }] : []),
      ]);
    }
    this.selectedNodeId.set(id);
    this.libraryOpen.set(false);
    this.markDirty();
  }

  deleteSelectedNode(): void {
    const node = this.selectedNode();
    if (!node || node.protected || this.readOnly()) return;
    const incoming = this.edges().find((edge) => edge.target === node.id);
    const outgoing = this.edges().find((edge) => edge.source === node.id);
    this.nodes.update((nodes) => nodes.filter((item) => item.id !== node.id));
    this.edges.update((edges) => [
      ...edges.filter((edge) => edge.source !== node.id && edge.target !== node.id),
      ...(incoming && outgoing ? [{
        id: `edge-${incoming.source}-${outgoing.target}-${Date.now()}`,
        source: incoming.source,
        target: outgoing.target,
        ...(incoming.sourcePort ? { sourcePort: incoming.sourcePort } : {}),
      }] : []),
    ]);
    this.selectedNodeId.set(null);
    this.markDirty();
  }

  updateName(event: Event): void {
    this.name.set(eventValue(event));
    this.markDirty();
  }

  updateDescription(event: Event): void {
    this.description.set(eventValue(event));
    this.markDirty();
  }

  updateNodeName(event: Event): void {
    this.patchSelected({ name: eventValue(event) });
  }

  updateConfig(key: string, event: Event): void {
    this.patchSelectedConfig(key, eventValue(event));
  }

  updateJsonConfig(key: string, event: Event): void {
    const node = this.selectedNode();
    if (!node) return;
    const errorKey = `${node.id}:${key}`;
    try {
      const value = eventValue(event).trim();
      const parsed = value ? JSON.parse(value) as unknown : {};
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('object_required');
      this.patchSelectedConfig(key, parsed);
      this.jsonErrors.update((errors) => {
        const next = { ...errors };
        delete next[errorKey];
        return next;
      });
    } catch {
      this.jsonErrors.update((errors) => ({ ...errors, [errorKey]: 'Use um objeto JSON válido.' }));
    }
  }

  jsonError(key: string): string | null {
    const nodeId = this.selectedNodeId();
    return nodeId ? this.jsonErrors()[`${nodeId}:${key}`] ?? null : null;
  }

  updateNumberConfig(key: string, event: Event): void {
    const value = Number(eventValue(event));
    this.patchSelectedConfig(key, Number.isFinite(value) ? value : 0);
  }

  updateModel(event: Event): void {
    const node = this.selectedNode();
    if (!node) return;
    const model = eventValue(event);
    const selected = this.models().find((item) => item.modelKey === model);
    this.patchSelectedConfig('modelPolicy', {
      ...recordValue(node.config['modelPolicy']),
      model,
      provider: selected?.provider ?? 'openrouter',
    });
  }

  updateModelPolicyNumber(key: 'temperature' | 'maxTokens', event: Event): void {
    const node = this.selectedNode();
    if (!node) return;
    const value = Number(eventValue(event));
    this.patchSelectedConfig('modelPolicy', {
      ...recordValue(node.config['modelPolicy']),
      [key]: Number.isFinite(value) ? value : 0,
    });
  }

  updateAllowTools(value: boolean): void {
    this.patchSelectedConfig('allowTools', value);
  }

  issueForNode(nodeId: string): ArchetypeValidationIssue | null {
    return this.issues().find((issue) => issue.nodeId === nodeId) ?? null;
  }

  nodeIcon(type: ArchetypeNodeType): string {
    return NODE_ICONS[type];
  }

  nodeTypeLabel(type: ArchetypeNodeType): string {
    return NODE_LABELS[type];
  }

  inputId(node: ArchetypeGraphNode): string {
    return `${node.id}::in`;
  }

  outputId(node: ArchetypeGraphNode, port = 'out'): string {
    return `${node.id}::${port}`;
  }

  edgeOutputId(edge: ArchetypeGraphEdge): string {
    return `${edge.source}::${edge.sourcePort ?? 'out'}`;
  }

  edgeInputId(edge: ArchetypeGraphEdge): string {
    return `${edge.target}::in`;
  }

  configText(node: ArchetypeGraphNode, key: string): string {
    const value = node.config[key];
    if (typeof value === 'string') return value;
    if (value === undefined || value === null) return '';
    return JSON.stringify(value, null, 2);
  }

  modelPolicyValue(node: ArchetypeGraphNode, key: string): string | number {
    const value = recordValue(node.config['modelPolicy'])[key];
    return typeof value === 'string' || typeof value === 'number' ? value : '';
  }

  orderedNodes(): ArchetypeGraphNode[] {
    const byId = new Map(this.nodes().map((node) => [node.id, node]));
    const outgoing = new Map(this.edges().map((edge) => [edge.source, edge.target]));
    const ordered: ArchetypeGraphNode[] = [];
    const seen = new Set<string>();
    let current: string | undefined = this.nodes().find((node) => node.type === 'start')?.id ?? this.nodes()[0]?.id;
    while (current && !seen.has(current)) {
      seen.add(current);
      const node = byId.get(current);
      if (node) ordered.push(node);
      current = outgoing.get(current);
    }
    return [...ordered, ...this.nodes().filter((node) => !seen.has(node.id))];
  }

  private load(id: string): void {
    this.loading.set(true);
    forkJoin({ archetype: this.api.getArchetype(id), models: this.api.listApprovedLlmModels() })
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.loading.set(false)))
      .subscribe({
        next: ({ archetype, models }) => {
          this.archetype.set(archetype);
          this.name.set(archetype.name);
          this.description.set(archetype.description ?? '');
          this.models.set(models.data);
          const version = archetype.draftVersion ?? archetype.currentPublishedVersion;
          if (version) this.applyVersion(version);
        },
        error: () => {
          this.toast.error('Não foi possível carregar o arquétipo.');
          this.back();
        },
      });
  }

  private applyVersion(version: AgentArchetypeVersion): void {
    this.version.set(version);
    this.nodes.set(structuredClone(version.graphSpec.nodes));
    this.edges.set(structuredClone(version.graphSpec.edges));
    this.issues.set(version.validationReport.issues ?? []);
    this.selectedNodeId.set(null);
    this.jsonErrors.set({});
    this.dirty.set(false);
  }

  private persistDraft(): Observable<AgentArchetypeVersion> {
    const archetype = this.archetype();
    const version = this.version();
    if (!archetype || !version || this.readOnly()) {
      return version ? of(version) : new Observable((subscriber) => subscriber.error(new Error('Draft unavailable')));
    }
    if (this.hasJsonErrors()) return throwError(() => new Error('Invalid JSON configuration'));
    if (!this.dirty()) return of(version);
    const graphSpec: ArchetypeGraphSpec = {
      schemaVersion: 1,
      entryNodeId: this.nodes().find((node) => node.type === 'start')?.id ?? version.graphSpec.entryNodeId,
      nodes: this.nodes(),
      edges: this.edges(),
    };
    return this.api.updateArchetype(archetype.id, {
      name: this.name().trim(),
      description: this.description().trim() || null,
    }).pipe(
      switchMap(() => this.api.saveArchetypeDraft(archetype.id, version.id, {
        expectedRevision: version.revision,
        graphSpec,
        goalPolicy: version.goalPolicy,
        persona: version.persona,
        channelPolicy: version.channelPolicy,
        toolPolicy: version.toolPolicy,
        retrievalPolicy: version.retrievalPolicy,
      })),
      tap((saved) => {
        this.version.set(saved);
        this.archetype.update((current) => current ? {
          ...current,
          name: this.name().trim(),
          description: this.description().trim() || null,
          draftVersion: saved,
        } : current);
        this.issues.set([]);
        this.dirty.set(false);
      }),
    );
  }

  private ensureValidated(version: AgentArchetypeVersion): Observable<AgentArchetypeVersion> {
    if (version.validationStatus === 'valid' && version.validatedGraphHash === version.graphHash) return of(version);
    return this.api.validateArchetypeDraft(version.archetypeId, version.id, version.revision).pipe(
      tap((result) => this.issues.set(result.issues)),
      map((result) => {
        if (!result.valid) throw new Error('invalid_graph');
        this.version.set(result.version);
        return result.version;
      }),
    );
  }

  private patchSelected(patch: Partial<ArchetypeGraphNode>): void {
    const id = this.selectedNodeId();
    if (!id || this.readOnly()) return;
    this.nodes.update((nodes) => nodes.map((node) => node.id === id ? { ...node, ...patch } : node));
    this.markDirty();
  }

  private patchSelectedConfig(key: string, value: unknown): void {
    const node = this.selectedNode();
    if (!node) return;
    this.patchSelected({ config: { ...node.config, [key]: value } });
  }

  private markDirty(): void {
    if (!this.readOnly()) this.dirty.set(true);
  }

  private handleWriteError(error: { status?: number }): void {
    if (error.status === 409) {
      this.toast.error('Este rascunho mudou em outra sessão. Recarregue antes de continuar.');
      return;
    }
    this.toast.error('Não foi possível concluir a ação.');
  }
}

function defaultConfig(type: ArchetypeNodeType, model = 'google/gemini-3.1-flash-lite'): Record<string, unknown> {
  if (type === 'llm' || type === 'response') {
    return {
      prompt: type === 'response' ? 'Componha uma resposta clara e objetiva.' : 'Analise o contexto e produza a próxima decisão.',
      modelPolicy: { provider: 'openrouter', model, temperature: 0.2, maxTokens: type === 'response' ? 900 : 1200 },
      allowTools: type === 'llm',
      outputSchema: {},
    };
  }
  if (type === 'condition') return { sourceNodeId: '', path: 'content', operator: 'exists', value: '' };
  if (type === 'knowledge') return { queryTemplate: '{{message}}', topK: 6, inputMapping: {}, outputMapping: {} };
  if (type === 'tool') return { toolKey: '', inputMapping: {}, outputMapping: {} };
  if (type === 'handoff') return { targetId: '', rule: '' };
  return {};
}

function eventValue(event: Event): string {
  return (event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value;
}

function portNodeId(portId: string | null | undefined): string | null {
  return portId?.split('::')[0] || null;
}

function recordValue(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}
